import { useCallback, useEffect, useMemo, useState } from "react";

import { Button } from "@/components/ui/button/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog/dialog";
import { InputTextField } from "@/components/ui/form/fields/input-field/input-text";
import { SelectField } from "@/components/ui/form/fields/select-field";
import { TextAreaField } from "@/components/ui/form/fields/text-area-field";
import { DatePicker } from "@/components/ui/input/date-picker-single";

import * as Model from "../model";

import type * as Repository from "../../_repository";
import type { SetStateAction } from "react";

export type Props = {
	hotelID: string;
	onCloseCallback: () => void;
	optionPmsGetAvailableOptionListQueryData: Repository.Query.QueryData.OptionPmsGetAvailableOptionList.OptionPmsGetAvailableOptionListQueryData;
	initSearchDate: string;
	onQueryOptionPmsGetAvailableOptionList: (props: { hotelID: string; date: string }) => Promise<void>;
};

export const CreateOptionPmsPresenter = ({
	hotelID,
	onCloseCallback,
	optionPmsGetAvailableOptionListQueryData,
	initSearchDate,
	onQueryOptionPmsGetAvailableOptionList,
}: Props) => {
	/**
	 * 初期化
	 */
	const {
		//
		viewData,
		//
		viewModel,
		// ゲスト名の変更
		onChangeGuestName,
		// 電話番号の変更
		onChangeGuestTel,
		// 日付の変更
		onChangeDate,
		// オプションの変更
		onChangeOption,
		// サブオプションの変更
		onChangeSubOption,
		// 数量の変更
		onChangeAmount,
		// コメントの変更
		onChangeComment,
		//
		// オプション作成
		onCreateOptionPms,
	} = Model.UseCase.useInitialized({
		hotelID,
		optionPmsGetAvailableOptionListQueryData,
		initSearchDate,
		onQueryOptionPmsGetAvailableOptionList,
	});
	const [open, setOpen] = useState(true);
	/**
	 * ダイアログの表示制御
	 */
	const onOpenChange = useCallback((value: boolean) => {
		setOpen(value);
	}, []);
	useEffect(() => {
		if (open) return;

		onCloseCallback();
	}, [onCloseCallback, open]);

	/**
	 * お客様のお名前の変更
	 */
	const handleChangeGuestName = useCallback(
		(value: string) => {
			onChangeGuestName(value);
		},
		[onChangeGuestName],
	);

	/**
	 * 電話番号
	 */
	const handleChangeGuestTel = useCallback(
		(value: string) => {
			onChangeGuestTel(value);
		},
		[onChangeGuestTel],
	);

	/**
	 * 日付の変更
	 */
	const handleChangeDate = useCallback(
		(value: SetStateAction<string>) => {
			onChangeDate(value as string);
		},
		[onChangeDate],
	);

	/**
	 * オプションの変更
	 */
	const handleChangeOption = useCallback(
		(value: string) => {
			onChangeOption(value);
		},
		[onChangeOption],
	);

	/**
	 * サブオプションの変更
	 */
	const handleChangeSubOption = useCallback(
		(value: string) => {
			onChangeSubOption(value);
		},
		[onChangeSubOption],
	);

	/**
	 * 数量の変更
	 */
	const handleChangeAmount = useCallback(
		(value: string) => {
			onChangeAmount(Number(value));
		},
		[onChangeAmount],
	);

	/**
	 * メモの変更
	 */
	const handleChangeMemo = useCallback(
		(value: string) => {
			onChangeComment(value);
		},
		[onChangeComment],
	);

	/**
	 * 作成ボタンのクリックアクション
	 */
	const handleClickCreate = useCallback(() => {
		const successCallback = () => onOpenChange(false);
		onCreateOptionPms(successCallback);
	}, [onCreateOptionPms, onOpenChange]);

	const disabled = useMemo(
		() =>
			!!viewModel.guestName.errorMessage ||
			!!viewModel.optionID.errorMessage ||
			!!viewModel.subOptionID?.errorMessage ||
			!!viewModel.amount.errorMessage,
		[
			viewModel.amount.errorMessage,
			viewModel.guestName.errorMessage,
			viewModel.optionID.errorMessage,
			viewModel.subOptionID?.errorMessage,
		],
	);

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="overflow-y-scroll text-base md:max-w-2xl">
				<DialogHeader>
					<DialogTitle>オプションの新規予約</DialogTitle>
				</DialogHeader>

				<div className="border-b border-zinc-300" />

				<div className="grid space-y-5 px-6">
					<div className="grid grid-cols-2 gap-x-4">
						{/* お客様のお名前 */}
						<InputTextField
							value={viewModel.guestName.value}
							label="お客様のお名前"
							errorMessage={viewModel.guestName.errorMessage}
							handleChange={handleChangeGuestName}
							className="text-base"
						/>

						{/* 電話番号 */}
						<InputTextField
							value={viewModel.guestTel?.value}
							errorMessage={viewModel.guestTel?.errorMessage}
							label="電話番号"
							handleChange={handleChangeGuestTel}
							className="text-base"
						/>
					</div>

					{/* 日付を選択 */}
					<div className="w-full space-y-1">
						<p className="text-sm">日付を選択</p>
						<DatePicker className="w-full" selected={viewModel.date} onSelect={handleChangeDate} />
						<p className="text-sm text-brand">選択した日付が正しいかどうかご確認ください</p>
					</div>

					{/* オプションを選択 */}
					<SelectField
						label="オプションを選択"
						value={viewModel.optionID.value}
						options={viewData.optionList}
						errorMessage={viewModel.optionID.errorMessage}
						handleValueChange={handleChangeOption}
					/>

					{/* サブオプションを選択 */}
					{viewData.subOptionList && (
						<SelectField
							label="サブオプションを選択"
							value={viewModel.subOptionID?.value}
							options={viewData.subOptionList}
							errorMessage={viewModel.subOptionID?.errorMessage}
							handleValueChange={handleChangeSubOption}
						/>
					)}

					{/* 数量を選択 */}
					{viewData.amountList && (
						<SelectField
							label="数量を選択"
							value={viewModel.amount.value}
							options={viewData.amountList}
							errorMessage={viewModel.amount.errorMessage}
							handleValueChange={handleChangeAmount}
						/>
					)}

					{/* メモ */}
					<TextAreaField
						label="メモ"
						value={viewModel.comment?.value}
						errorMessage={viewModel.comment?.errorMessage}
						handleChange={handleChangeMemo}
					/>

					<div className="text-center">
						<Button
							variant="default"
							size="lg"
							type="button"
							disabled={disabled}
							className="text-base"
							onClick={handleClickCreate}
						>
							作成
						</Button>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
};
